import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, TextField, Typography, FormControl, InputLabel, Select, MenuItem,
  FormHelperText,
  Divider, Alert, Box, Link as MuiLink, alpha, useTheme
} from '@mui/material';
import { PersonAddAlt, BadgeOutlined, EventNote } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { SessionClass, SectionInstance, Session } from '../../types/sessions';
import { studentsApi } from '../../services/sessions/studentsApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import settingsService from '../../services/settingsService';
import { Group } from '../../types';

interface NewStudentDialogProps {
  open: boolean;
  activeSession: Session | null;
  defaultClassId?: string;
  defaultSectionInstanceId?: string | null;
  classes: SessionClass[];
  onClose: () => void;
  onCreated: () => void;
}

interface FormValues {
  name: string;
  fatherName: string;
  fatherPhone: string;
  phone: string;
  address: string;
  groupId: string;             // '' = unassigned
  classId: string;
  sectionInstanceId: string;   // '' = none/section-less
  rollNumber: string;          // '' = auto
}

const validationSchema = yup.object({
  name:        yup.string().trim().required('Required').max(120),
  fatherName:  yup.string().trim().required('Required').max(120),
  fatherPhone: yup.string().trim().required('Required').max(40),
  phone:       yup.string().trim().required('Required').max(40),
  address:     yup.string().trim().required('Required').max(300),
  classId:     yup.string().required('Pick a class'),
  rollNumber:  yup.string().matches(/^$|^[1-9]\d*$/, 'Must be a positive number')
});

const NewStudentDialog: React.FC<NewStudentDialogProps> = ({
  open, activeSession, defaultClassId, defaultSectionInstanceId, classes, onClose, onCreated
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [showRollOverride, setShowRollOverride] = useState(false);
  // Set to true by the "Save & add another" button right before submit. Read
  // inside onSubmit to decide whether to close the dialog or just clear the
  // identity fields and keep class/section for the next entry.
  const addAnotherRef = useRef<boolean>(false);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const initialValues = useMemo<FormValues>(() => ({
    name: '', fatherName: '', fatherPhone: '', phone: '', address: '',
    groupId: '',
    classId: defaultClassId ?? '',
    sectionInstanceId: (defaultSectionInstanceId ?? '') as string,
    rollNumber: ''
  }), [defaultClassId, defaultSectionInstanceId]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    // Section is mandatory when the picked class actually has sections
    // (matches the BE rule). Yup can't see the async-loaded `sections`
    // state, so we validate here — clean per-field error surfaces on the
    // dropdown without needing a stateful yup schema rebuild.
    validate: (values) => {
      const errors: Partial<Record<keyof FormValues, string>> = {};
      if (values.classId && sections.length > 0 && !values.sectionInstanceId) {
        errors.sectionInstanceId = 'Section is required for this class';
      }
      return errors;
    },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!activeSession) return;
      const addAnother = addAnotherRef.current;
      addAnotherRef.current = false;
      try {
        const result = await studentsApi.createWithEnrollment({
          student: {
            name: values.name.trim(),
            fatherName: values.fatherName.trim(),
            fatherPhone: values.fatherPhone.trim(),
            phone: values.phone.trim(),
            address: values.address.trim(),
            groupId: values.groupId || null
          },
          initialEnrollment: {
            sessionId: activeSession._id,
            classId: values.classId,
            sectionInstanceId: values.sectionInstanceId || null,
            rollNumber: values.rollNumber ? Number(values.rollNumber) : undefined
          }
        });
        enqueueSnackbar(
          `${values.name.trim()} enrolled as Roll ${result.enrollment.rollNumber}`,
          { variant: 'success' }
        );
        onCreated();
        if (addAnother) {
          // Reset identity fields but keep class/section so admin can blast
          // through several students in the same slot without re-picking.
          resetForm({
            values: {
              name: '',
              fatherName: '',
              fatherPhone: '',
              phone: '',
              address: '',
              groupId: values.groupId,
              classId: values.classId,
              sectionInstanceId: values.sectionInstanceId,
              rollNumber: ''
            }
          });
          setShowRollOverride(false);
          // Focus name for instant typing.
          window.requestAnimationFrame(() => nameInputRef.current?.focus());
        } else {
          resetForm();
          onClose();
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not create student', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Load groups once when the dialog opens. Used for the optional Group
  // picker in the identity section.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await settingsService.getGroups({ isActive: true, limit: 200 });
        if (!cancelled) setGroups(res?.data?.groups || []);
      } catch { /* silent — picker shows empty list */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Load sections whenever the chosen class changes.
  useEffect(() => {
    if (!open || !activeSession || !formik.values.classId) {
      setSections([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const secs = await sectionInstancesApi.list({
          sessionId: activeSession._id,
          classId: formik.values.classId
        });
        if (!cancelled) setSections(secs);
      } catch { /* silent — empty array is the fallback */ }
    })();
    return () => { cancelled = true; };
  }, [open, activeSession, formik.values.classId]);

  // If section list changes and the currently picked section is no longer
  // valid for the chosen class, reset it.
  useEffect(() => {
    if (formik.values.sectionInstanceId &&
        !sections.find(s => s._id === formik.values.sectionInstanceId)) {
      formik.setFieldValue('sectionInstanceId', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  if (!activeSession) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>No active session</DialogTitle>
        <DialogContent>
          <Alert severity="warning" variant="outlined">
            Activate a session before enrolling new students.
          </Alert>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={formik.isSubmitting ? undefined : onClose} maxWidth="md" fullWidth>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <PersonAddAlt color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={600}>Add new student</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Creates the student record and their first enrollment in <strong>{activeSession.name}</strong> together.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3}>
            {/* Section 1 — Identity */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <BadgeOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                  Student identity
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    name="name" label="Full name" autoFocus fullWidth
                    inputRef={nameInputRef}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                  />
                  <TextField
                    name="phone" label="Student phone" fullWidth
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    name="fatherName" label="Father's name" fullWidth
                    value={formik.values.fatherName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.fatherName && Boolean(formik.errors.fatherName)}
                    helperText={formik.touched.fatherName && formik.errors.fatherName}
                  />
                  <TextField
                    name="fatherPhone" label="Father's phone" fullWidth
                    value={formik.values.fatherPhone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.fatherPhone && Boolean(formik.errors.fatherPhone)}
                    helperText={formik.touched.fatherPhone && formik.errors.fatherPhone}
                  />
                </Stack>
                <TextField
                  name="address" label="Address" multiline minRows={2} fullWidth
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                />
                <FormControl fullWidth>
                  <InputLabel>Group (optional)</InputLabel>
                  <Select
                    name="groupId" label="Group (optional)"
                    value={formik.values.groupId}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {groups.map(g => (
                      <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            {/* Section 2 — First enrollment */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <EventNote sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                  First enrollment
                </Typography>
              </Stack>

              <Box sx={{
                px: 1.5, py: 1, mb: 2, borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`
              }}>
                <Typography variant="caption" color="text.secondary">
                  Will be enrolled in <strong>{activeSession.name}</strong>
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth error={formik.touched.classId && Boolean(formik.errors.classId)}>
                  <InputLabel>Class</InputLabel>
                  <Select
                    name="classId" label="Class"
                    value={formik.values.classId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {classes.map(c => (
                      <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  required={sections.length > 0}
                  disabled={!formik.values.classId || sections.length === 0}
                  error={
                    sections.length > 0 &&
                    formik.submitCount > 0 &&
                    Boolean(formik.errors.sectionInstanceId)
                  }
                >
                  <InputLabel>Section{sections.length > 0 ? '' : ''}</InputLabel>
                  <Select
                    name="sectionInstanceId" label="Section"
                    value={formik.values.sectionInstanceId}
                    onChange={formik.handleChange}
                  >
                    {/* Only offer the "No section" option when the class
                        genuinely has no sections. When sections exist, the
                        admin MUST pick one — matches the BE guard. */}
                    {sections.length === 0 ? (
                      <MenuItem value="">
                        <em>No sections in this class</em>
                      </MenuItem>
                    ) : (
                      <MenuItem value="" disabled>
                        <em>Select a section</em>
                      </MenuItem>
                    )}
                    {sections.map(s => (
                      <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                  {sections.length > 0 &&
                    formik.submitCount > 0 &&
                    formik.errors.sectionInstanceId && (
                    <FormHelperText>{formik.errors.sectionInstanceId as string}</FormHelperText>
                  )}
                </FormControl>
              </Stack>

              {/* Roll number — auto-assigned by default. When override is on,
                  the field is a full-width TextField that matches the visual
                  weight of Class/Section above (same width, same label style). */}
              {showRollOverride ? (
                <TextField
                  name="rollNumber"
                  label="Roll number"
                  placeholder="e.g. 12"
                  value={formik.values.rollNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.rollNumber && Boolean(formik.errors.rollNumber)}
                  helperText={
                    (formik.touched.rollNumber && formik.errors.rollNumber) || (
                      <Box component="span">
                        Positive integer — must be unique in this class/section.{' '}
                        <MuiLink
                          component="button"
                          type="button"
                          variant="caption"
                          onClick={(e) => { e.preventDefault(); setShowRollOverride(false); formik.setFieldValue('rollNumber', ''); }}
                          sx={{ textDecorationStyle: 'dotted' }}
                        >
                          Use auto-assignment instead
                        </MuiLink>
                      </Box>
                    )
                  }
                  fullWidth
                  autoFocus
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Roll number will be auto-assigned.{' '}
                  <MuiLink
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => setShowRollOverride(true)}
                    sx={{ textDecorationStyle: 'dotted' }}
                  >
                    Set manually instead
                  </MuiLink>
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={formik.isSubmitting}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          <Button
            type="submit"
            variant="outlined"
            disabled={formik.isSubmitting}
            onClick={() => { addAnotherRef.current = true; }}
            sx={{ borderRadius: 2 }}
          >
            Save & add another
          </Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={formik.isSubmitting}
            sx={{ borderRadius: 2 }}
          >
            {formik.isSubmitting ? 'Creating…' : 'Create & enroll'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NewStudentDialog;
